import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { LuArrowDown, LuArrowUp, LuArrowUpDown, LuChevronRight, LuRefreshCw } from 'react-icons/lu';
import Select from '../ui/Select';
import { useNavigate } from 'react-router';
import axiosInstance from '../../api/axiosInstance';
import { useProject } from '../../context/ProjectContext';
import useWorkspaceRouting from '../../hooks/useWorkspaceRouting';
import { buildWorkspaceDashboardPath } from '../../utils/workspaceRouteUtils';
import { SchedulesPanel } from './SchedulesWorkspace';
import {
  Breadcrumbs,
  DataTable,
  EmptyState,
  ErrorState,
  FilterBar,
  MetricCard,
  PageScaffold,
  Pagination,
  SearchInput,
  StatusBadge,
  Tabs,
} from '../ui';
import { formatDateTime } from '../../utils/dateTimeFormat';

const formatDuration = (seconds) => {
  const n = Number(seconds);
  if (!Number.isFinite(n) || n <= 0) return '—';
  if (n < 60) return `${n.toFixed(1)}s`;
  return `${Math.floor(n / 60)}m ${Math.round(n % 60)}s`;
};

const normalizeStatus = (s) => String(s || '').toLowerCase();

const isLive = (status) =>
  ['running', 'in_progress', 'pending', 'queued', 'started'].includes(normalizeStatus(status));

const isHistory = (status) =>
  ['passed', 'failed', 'completed', 'aborted', 'skipped', 'error', 'success'].includes(
    normalizeStatus(status)
  );

const statusKind = (status) => {
  const s = normalizeStatus(status);
  if (!s) return 'unknown';
  if (isLive(s)) return 'running';
  if (['failed', 'fail', 'error'].includes(s)) return 'failed';
  if (['passed', 'pass', 'success', 'completed'].includes(s)) return 'passed';
  return 'other';
};

const statusLabel = (status) => {
  const s = normalizeStatus(status).replace(/_/g, ' ');
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
};

const toCount = (...values) => {
  for (const v of values) {
    if (v != null && v !== '' && Number.isFinite(Number(v))) return Number(v);
  }
  return null;
};

// Plan/suite histories report `execution_status`; older payloads used `status`.
const resolveStatus = (row, passed, failed, total) => {
  const explicit = row.status || row.execution_status;
  if (explicit) return explicit;
  if (failed > 0) return 'failed';
  if (total > 0 && passed === total) return 'passed';
  return null;
};

const personName = (value) => (typeof value === 'string' ? value : value?.name || null);

const mapGroupRun = (row, { idKey, type, name, entityId }) => {
  const passed = toCount(row.passed, row.total_passed);
  const failed = toCount(row.failed, row.total_failed);
  const skipped = toCount(row.skipped, row.total_skipped);
  const total = toCount(row.total_test_cases, row.total, row.total_cases);
  const executionId = row[idKey] || row.execution_id || row.id;
  return {
    id: `${type}-${executionId}`,
    executionId,
    name,
    type,
    entityId,
    status: resolveStatus(row, passed, failed, total),
    environment: row.environment || row.environment_name || row.env || null,
    startedAt: row.started_at || row.created_at || row.start_time,
    duration: row.duration || row.execution_time_sec || row.duration_sec,
    passed,
    failed,
    skipped,
    total,
    passRate: toCount(row.pass_rate),
    triggeredBy: personName(row.executed_by_name) || personName(row.triggered_by) || personName(row.created_by),
    raw: row,
  };
};

const relativeTime = (value) => {
  if (!value) return null;
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return null;
  const diff = (then - Date.now()) / 1000;
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto', style: 'short' });
  const units = [
    ['year', 31536000],
    ['month', 2592000],
    ['week', 604800],
    ['day', 86400],
    ['hour', 3600],
    ['minute', 60],
  ];
  for (const [unit, secs] of units) {
    if (Math.abs(diff) >= secs) return rtf.format(Math.round(diff / secs), unit);
  }
  return 'just now';
};

const passRateOf = (row) => {
  if (row.passRate != null) return row.passRate;
  const total = Math.max(row.total || 0, (row.passed || 0) + (row.failed || 0) + (row.skipped || 0));
  return total ? ((row.passed || 0) / total) * 100 : null;
};

const SORTERS = {
  name: (r) => String(r.name || '').toLowerCase(),
  status: (r) => statusKind(r.status),
  startedAt: (r) => (r.startedAt ? new Date(r.startedAt).getTime() : 0),
  duration: (r) => Number(r.duration) || 0,
  result: (r) => passRateOf(r) ?? -1,
};

const ResultCell = ({ row }) => {
  if (row.passed == null && row.failed == null && !row.progress) return <span className="rt2-muted">—</span>;
  const passed = row.passed || 0;
  const failed = row.failed || 0;
  const skipped = row.skipped || 0;
  const total = Math.max(row.total || 0, row.progress?.total || 0, passed + failed + skipped);
  const done = row.progress?.done != null ? row.progress.done : passed + failed + skipped;
  const notRun = Math.max(total - passed - failed - skipped, 0);
  const rate = passRateOf(row);
  const pct = (n) => (total ? `${(n / total) * 100}%` : '0%');
  const running = statusKind(row.status) === 'running';
  const summary = [
    `${passed} passed`,
    `${failed} failed`,
    row.skipped != null ? `${skipped} skipped` : null,
    notRun ? `${notRun} not run` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="rt-result" title={`${summary} of ${total}`}>
      <div className="rt-result__bar" aria-hidden="true">
        <span className="rt-result__seg rt-result__seg--pass" style={{ width: pct(passed) }} />
        <span className="rt-result__seg rt-result__seg--fail" style={{ width: pct(failed) }} />
        <span className="rt-result__seg rt-result__seg--skip" style={{ width: pct(skipped) }} />
      </div>
      <div className="rt-result__meta">
        <span className="rt-result__pass">{passed} passed</span>
        <span className={failed ? 'rt-result__fail' : 'rt-result__zero'}>{failed} failed</span>
        <span className="rt-result__total">of {total}</span>
        {running && total > 0 && (
          <span className="rt-result__rate">{done}/{total} done</span>
        )}
        {!running && rate != null && <span className="rt-result__rate">{Math.round(rate)}%</span>}
      </div>
    </div>
  );
};

const SortHeader = ({ label, sortKey, sort, onSort }) => {
  const active = sort.key === sortKey;
  const Icon = !active ? LuArrowUpDown : sort.dir === 'asc' ? LuArrowUp : LuArrowDown;
  return (
    <button
      type="button"
      className={`rt-sort-btn${active ? ' rt-sort-btn--active' : ''}`}
      onClick={() => onSort(sortKey)}
      aria-label={`Sort by ${label}`}
    >
      {label}
      <Icon size={12} />
    </button>
  );
};

/**
 * Unified executions feed from /dashboard_data/project/executions
 * (plan + standalone suite + standalone case runs in one request).
 */
const ExecutionsHub = () => {
  const navigate = useNavigate();
  const { selectedProject } = useProject();
  const { getWorkspaceIdFromURL, getProjectIdFromURL } = useWorkspaceRouting();
  const projectId = getProjectIdFromURL() || selectedProject?.id;

  const [tab, setTab] = useState('recent');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState({ key: 'startedAt', dir: 'desc' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [, setTick] = useState(0);

  const navigateTo = useCallback(
    (path) => {
      const workspaceId = getWorkspaceIdFromURL();
      if (workspaceId) {
        navigate(buildWorkspaceDashboardPath(workspaceId, projectId, path));
      } else {
        navigate(`/dashboard/${path}`);
      }
    },
    [navigate, getWorkspaceIdFromURL, projectId]
  );

  const load = useCallback(async () => {
    if (!projectId) {
      setRows([]);
      setLoading(false);
      setError('Select a project to view executions.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      // One project-scoped feed (plan + standalone suite + standalone case runs).
      const res = await axiosInstance.get('/dashboard_data/project/executions', {
        params: { project_id: projectId, limit: 150, per_source_limit: 100 },
        skipGlobalLoader: true,
        skipGlobalToast: true,
      });
      const list = res?.data?.executions || res?.executions || [];
      const unified = (Array.isArray(list) ? list : []).map((row) => {
        if (row.type === 'case') {
          const status = row.status || row.execution_status;
          const kind = statusKind(status);
          return {
            id: `case-${row.execution_id}`,
            executionId: row.execution_id,
            name: row.name || `Case ${row.entity_id}`,
            type: 'case',
            entityId: row.entity_id,
            status,
            environment: row.environment || null,
            startedAt: row.started_at || row.created_at,
            duration: row.duration,
            passed: row.passed != null ? Number(row.passed) : kind === 'passed' ? 1 : 0,
            failed: row.failed != null ? Number(row.failed) : kind === 'failed' ? 1 : 0,
            skipped:
              row.skipped != null
                ? Number(row.skipped)
                : normalizeStatus(status) === 'skipped'
                  ? 1
                  : null,
            total: row.total != null ? Number(row.total) : 1,
            passRate: toCount(row.pass_rate),
            triggeredBy: personName(row.executed_by_name),
            raw: row,
          };
        }
        const mapped = mapGroupRun(
          {
            ...row,
            execution_status: row.status,
            created_at: row.started_at,
            total_test_cases: row.total,
            executed_by_name: row.executed_by_name,
          },
          {
            idKey: 'execution_id',
            type: row.type,
            name: row.name,
            entityId: row.entity_id,
          }
        );
        if (row.progress) mapped.progress = row.progress;
        if (row.counts_source) mapped.countsSource = row.counts_source;
        return mapped;
      });

      setRows(unified);
      setUpdatedAt(Date.now());
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      const message =
        (typeof detail === 'object' && detail?.message) ||
        (typeof detail === 'string' && detail) ||
        err?.message ||
        'Failed to load executions';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  // Keep the "updated …" label fresh.
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    setPage(1);
  }, [tab, sourceFilter, statusFilter, query, sort]);

  const scoped = useMemo(() => {
    let list = rows;
    if (tab === 'live') list = list.filter((r) => isLive(r.status));
    if (tab === 'history') list = list.filter((r) => isHistory(r.status));
    if (sourceFilter !== 'all') list = list.filter((r) => r.type === sourceFilter);
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter((r) =>
        [r.name, r.status, r.environment, r.type, r.triggeredBy, r.executionId]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      );
    }
    return list;
  }, [rows, tab, sourceFilter, query]);

  const statusCounts = useMemo(() => {
    const counts = { all: scoped.length, passed: 0, failed: 0, running: 0 };
    scoped.forEach((r) => {
      const kind = statusKind(r.status);
      if (counts[kind] != null) counts[kind] += 1;
    });
    return counts;
  }, [scoped]);

  const filtered = useMemo(() => {
    const list = statusFilter === 'all' ? scoped : scoped.filter((r) => statusKind(r.status) === statusFilter);
    const get = SORTERS[sort.key] || SORTERS.startedAt;
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      const va = get(a);
      const vb = get(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [scoped, statusFilter, sort]);

  const pageRows = useMemo(
    () => filtered.slice((page - 1) * pageSize, page * pageSize),
    [filtered, page, pageSize]
  );

  const stats = useMemo(() => {
    const byType = { plan: 0, suite: 0, case: 0 };
    let live = 0;
    let failedRuns = 0;
    let finished = 0;
    let testsPassed = 0;
    let testsExecuted = 0;
    rows.forEach((r) => {
      byType[r.type] = (byType[r.type] || 0) + 1;
      const kind = statusKind(r.status);
      if (kind === 'running') live += 1;
      if (kind === 'failed') failedRuns += 1;
      if (kind === 'passed' || kind === 'failed') finished += 1;
      testsPassed += r.passed || 0;
      testsExecuted += (r.passed || 0) + (r.failed || 0);
    });
    return {
      ...byType,
      live,
      failedRuns,
      finished,
      testPassRate: testsExecuted ? Math.round((testsPassed / testsExecuted) * 100) : null,
    };
  }, [rows]);

  const onSort = (key) =>
    setSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'name' ? 'asc' : 'desc' }
    );

  const resetFilters = () => {
    setTab('recent');
    setSourceFilter('all');
    setStatusFilter('all');
    setQuery('');
  };

  const focusCard = (next) => {
    setTab(next.tab || 'recent');
    setSourceFilter(next.source || 'all');
    setStatusFilter(next.status || 'all');
  };

  const isCardActive = (spec) =>
    tab === (spec.tab || 'recent') &&
    sourceFilter === (spec.source || 'all') &&
    statusFilter === (spec.status || 'all');

  const filtersActive = tab !== 'recent' || sourceFilter !== 'all' || statusFilter !== 'all' || query.trim();

  const columns = [
    {
      key: 'name',
      header: <SortHeader label="Execution" sortKey="name" sort={sort} onSort={onSort} />,
      render: (row) => (
        <div className="rt-exec-name">
          <div className="rt-exec-name__title">{row.name}</div>
          <div className="rt2-muted">
            #{row.executionId}
            {row.triggeredBy ? ` · by ${row.triggeredBy}` : ''}
          </div>
        </div>
      ),
    },
    {
      key: 'type',
      header: 'Source',
      render: (row) => (
        <span className={`rt-badge rt-badge--${row.type === 'plan' ? 'primary' : row.type === 'suite' ? 'info' : 'neutral'}`}>
          {row.type}
        </span>
      ),
    },
    {
      key: 'status',
      header: <SortHeader label="Status" sortKey="status" sort={sort} onSort={onSort} />,
      render: (row) =>
        row.status ? (
          <span className={statusKind(row.status) === 'running' ? 'rt-exec-status--live' : undefined}>
            <StatusBadge status={row.status}>{statusLabel(row.status)}</StatusBadge>
          </span>
        ) : (
          <span className="rt2-muted" title="The API did not report a status for this run">
            Not reported
          </span>
        ),
    },
    {
      key: 'environment',
      header: 'Environment',
      render: (row) => row.environment || <span className="rt2-muted">—</span>,
    },
    {
      key: 'startedAt',
      header: <SortHeader label="Started" sortKey="startedAt" sort={sort} onSort={onSort} />,
      render: (row) =>
        row.startedAt ? (
          <div title={formatDateTime(row.startedAt)}>
            <div>{relativeTime(row.startedAt)}</div>
            <div className="rt2-muted">
              {formatDateTime(row.startedAt)}
            </div>
          </div>
        ) : (
          '—'
        ),
    },
    {
      key: 'duration',
      header: <SortHeader label="Duration" sortKey="duration" sort={sort} onSort={onSort} />,
      render: (row) => <span className="rt-num">{formatDuration(row.duration)}</span>,
    },
    {
      key: 'result',
      header: <SortHeader label="Result" sortKey="result" sort={sort} onSort={onSort} />,
      render: (row) => <ResultCell row={row} />,
    },
    {
      key: 'open',
      header: <span className="rt-visually-hidden">Open</span>,
      width: 36,
      render: () => (
        <span className="rt-exec-open" aria-hidden="true">
          <LuChevronRight size={16} />
        </span>
      ),
    },
  ];

  const openDetail = (row) => {
    navigateTo(
      `executions/${row.executionId}?type=${row.type}&entity_id=${row.entityId}&name=${encodeURIComponent(row.name)}`
    );
  };

  const cards = [
    {
      label: 'Total runs',
      value: rows.length,
      tone: 'primary',
      spec: {},
      context: stats.testPassRate != null ? `${stats.testPassRate}% of tests passed` : undefined,
    },
    {
      label: 'Live',
      value: stats.live,
      tone: 'success',
      spec: { tab: 'live' },
      context: stats.live ? 'running now' : 'nothing running',
      className: stats.live ? 'rt-metric-card--pulse' : '',
    },
    { label: 'Plan runs', value: stats.plan, tone: 'violet', spec: { source: 'plan' } },
    { label: 'Suite runs', value: stats.suite, tone: 'accent', spec: { source: 'suite' } },
    { label: 'Case runs', value: stats.case, tone: 'warning', spec: { source: 'case' } },
    {
      label: 'Failed',
      value: stats.failedRuns,
      tone: 'danger',
      spec: { status: 'failed' },
      context: stats.finished ? `of ${stats.finished} finished` : undefined,
    },
  ];

  const initialLoading = loading && rows.length === 0 && !error;

  return (
    <PageScaffold
      title="Executions"
      description="Monitor live and historical test runs across plans, suites, and cases."
      breadcrumbs={<Breadcrumbs items={[{ label: 'Run' }, { label: 'Executions' }]} />}
      actions={
        <>
          {updatedAt && (
            <span className="rt2-muted rt-exec-updated" aria-live="polite">
              {loading ? 'Refreshing…' : `Updated ${relativeTime(updatedAt)}`}
            </span>
          )}
          <button type="button" className="rt-btn rt-btn--secondary" onClick={load} disabled={loading}>
            <LuRefreshCw size={14} className={loading ? 'rt-spin' : undefined} /> Refresh
          </button>
          <button type="button" className="rt-btn rt-btn--primary" onClick={() => navigateTo('test-plans')}>
            Run Tests
          </button>
        </>
      }
    >
      <div className="rt-metric-grid">
        {cards.map((card) => (
          <MetricCard
            key={card.label}
            label={card.label}
            value={initialLoading ? '…' : card.value}
            tone={card.tone}
            context={initialLoading ? undefined : card.context}
            className={`${card.className || ''}${isCardActive(card.spec) && card.label !== 'Total runs' ? ' rt-metric-card--active' : ''}`}
            onClick={() => (isCardActive(card.spec) ? resetFilters() : focusCard(card.spec))}
          />
        ))}
      </div>

      <Tabs
        activeId={tab}
        onChange={setTab}
        tabs={[
          { id: 'live', label: 'Live', count: stats.live },
          { id: 'recent', label: 'Recent', count: rows.length },
          { id: 'scheduled', label: 'Scheduled' },
          { id: 'history', label: 'History' },
        ]}
      />

      {tab === 'scheduled' ? (
        <div style={{ marginTop: 12 }}>
          <SchedulesPanel />
        </div>
      ) : (
        <>
          <FilterBar>
            <SearchInput value={query} onChange={setQuery} placeholder="Search name, status, environment, user…" />
            <Select
              className="rt-select--filter"
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              aria-label="Source filter"
            >
              <option value="all">All sources</option>
              <option value="plan">Plans</option>
              <option value="suite">Suites</option>
              <option value="case">Cases</option>
            </Select>
            <div className="rt-chip-group" role="radiogroup" aria-label="Status filter">
              {[
                { id: 'all', label: 'All' },
                { id: 'passed', label: 'Passed' },
                { id: 'failed', label: 'Failed' },
                { id: 'running', label: 'Running' },
              ].map((chip) => (
                <button
                  key={chip.id}
                  type="button"
                  role="radio"
                  aria-checked={statusFilter === chip.id}
                  className={`rt-chip rt-chip--${chip.id}${statusFilter === chip.id ? ' rt-chip--active' : ''}`}
                  onClick={() => setStatusFilter(chip.id)}
                >
                  {chip.id !== 'all' && <span className="rt-chip__dot" aria-hidden="true" />}
                  {chip.label}
                  <span className="rt-chip__count">{statusCounts[chip.id] ?? 0}</span>
                </button>
              ))}
            </div>
            {filtersActive && (
              <button type="button" className="rt-btn rt-btn--tertiary rt-btn--sm" onClick={resetFilters}>
                Clear filters
              </button>
            )}
          </FilterBar>

          {!loading && error && (
            <ErrorState
              title={
                error?.includes('Select a project')
                  ? 'Unable to load executions'
                  : 'Could not load executions'
              }
              description={
                error?.includes('Select a project')
                  ? 'Please select a project before continuing.'
                  : error
              }
              onRetry={load}
            />
          )}
          {!error && (initialLoading || filtered.length > 0 || rows.length === 0) && (
            <DataTable
              columns={columns}
              rows={pageRows}
              rowKey="id"
              loading={initialLoading}
              onRowClick={openDetail}
              emptyTitle="No executions yet"
              emptyDescription="Run a plan, suite or case to populate this hub."
            />
          )}
          {!error && !initialLoading && rows.length > 0 && filtered.length === 0 && (
            <EmptyState
              title="No executions match these filters"
              description="Try a different status, source or search term."
              action={
                <button type="button" className="rt-btn rt-btn--secondary rt-btn--sm" onClick={resetFilters}>
                  Clear filters
                </button>
              }
            />
          )}
          {!error && filtered.length > 10 && (
            <Pagination
              page={page}
              pageSize={pageSize}
              total={filtered.length}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
              pageSizeOptions={[10, 20, 50, 100]}
            />
          )}
        </>
      )}
    </PageScaffold>
  );
};

export default ExecutionsHub;
